import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Descriptions,
  Divider,
  Drawer,
  Empty,
  Input,
  List,
  Space,
  Spin,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  FilePdfOutlined,
  PlusCircleOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import API_CONFIG from "../config/api.js";
import VentePaymentForm from "../gestion-vente/components/VentePaymentForm";
import { useAuth } from "../context/AuthContext";
import InvoiceNumberCorrection from "../components/invoices/InvoiceNumberCorrection";

const BASE_URL = API_CONFIG.BASE_URL;

interface PosCustomerOption {
  ID_Client: number;
  Nom_Raison_Sociale: string;
}

interface PosReprintCenterProps {
  open: boolean;
  onClose: () => void;
  companyId: number | null;
  warehouseId: number | null;
  warehouseData?: any;
  customers: PosCustomerOption[];
  isMobile: boolean;
  onOpenReceipt: (receiptData: any) => void;
}

interface PosOrderSummary {
  id: number;
  invoice_number: string;
  order_date: string;
  user_id?: number | null;
  total?: number;
  paid_amount?: number;
  payment_status?: string;
  order_status?: string;
  warehouse_name?: string;
  client_name?: string;
}

type ReprintPeriod = "today" | "7d" | "30d";

const formatAmount = (value: any): string =>
  Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

const formatDateForQuery = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const getDateRange = (period: ReprintPeriod) => {
  const now = new Date();
  const end = formatDateForQuery(now);

  if (period === "today") {
    return {
      dateDebut: end,
      dateFin: end,
    };
  }

  const start = new Date(now);
  start.setDate(now.getDate() - (period === "7d" ? 6 : 29));

  return {
    dateDebut: formatDateForQuery(start),
    dateFin: end,
  };
};

const getReadableErrorMessage = async (response: Response, fallback: string) => {
  try {
    const payload = await response.json();
    return payload?.message || payload?.error || payload?.details || fallback;
  } catch (error) {
    return fallback;
  }
};

const resolveClientName = (
  order: any,
  customers: PosCustomerOption[] = []
): string => {
  if (order?.client_name) return order.client_name;
  if (order?.entity_name) return order.entity_name;
  if (order?.user_name) return order.user_name;

  const customer = customers.find(
    (item) => Number(item.ID_Client) === Number(order?.user_id)
  );

  return customer?.Nom_Raison_Sociale || "Client inconnu";
};

const getPaymentStatusTag = (status?: string) => {
  if (status === "Payé") return <Tag color="green">Payé</Tag>;
  if (status === "Partiellement payé" || status === "Partiel") {
    return <Tag color="orange">Partiellement payé</Tag>;
  }
  if (status === "Non payé") return <Tag color="red">Non payé</Tag>;
  return <Tag>{status || "Non défini"}</Tag>;
};

const getOrderStatusTag = (status?: string) => {
  if (status === "Livré") return <Tag color="green">Livré</Tag>;
  if (status === "En attente") return <Tag color="orange">En attente</Tag>;
  if (status === "Annulé") return <Tag color="red">Annulé</Tag>;
  if (status === "Commandé") return <Tag color="blue">Commandé</Tag>;
  return <Tag>{status || "Non défini"}</Tag>;
};

const PosReprintCenter: React.FC<PosReprintCenterProps> = ({
  open,
  onClose,
  companyId,
  warehouseId,
  warehouseData,
  customers,
  isMobile,
  onOpenReceipt,
}) => {
  const { user } = useAuth();
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [actionLoading, setActionLoading] = useState<"receipt" | "invoice" | null>(
    null
  );
  const [searchValue, setSearchValue] = useState("");
  const [period, setPeriod] = useState<ReprintPeriod>("today");
  const [orders, setOrders] = useState<PosOrderSummary[]>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [selectedOrderDetail, setSelectedOrderDetail] = useState<any>(null);
  const [listError, setListError] = useState("");
  const [detailError, setDetailError] = useState("");
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);

  const fetchWarehouseDetails = useCallback(
    async (targetWarehouseId: number | null) => {
      if (!targetWarehouseId) return warehouseData || null;

      if (
        warehouseData &&
        Number(warehouseData.id) === Number(targetWarehouseId)
      ) {
        return warehouseData;
      }

      const response = await fetch(
        `${BASE_URL}/api/warehouses/${targetWarehouseId}`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getReadableErrorMessage(
            response,
            "Impossible de charger l'établissement pour la facture."
          )
        );
      }

      return response.json();
    },
    [warehouseData]
  );

  const fetchOrders = useCallback(async () => {
    if (!open || !companyId || !warehouseId) {
      setOrders([]);
      setSelectedOrderId(null);
      setSelectedOrderDetail(null);
      return;
    }

    try {
      setLoadingList(true);
      setListError("");

      const range = getDateRange(period);
      const params = new URLSearchParams({
        order_type: "POS",
        sort_by: "order_date",
        order: "desc",
        page: "1",
        limit: "40",
        warehouse: String(warehouseId),
        company_id: String(companyId),
        dateDebut: range.dateDebut,
        dateFin: range.dateFin,
      });

      const response = await fetch(`${BASE_URL}/api/orders?${params.toString()}`, {
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(
          await getReadableErrorMessage(
            response,
            "Impossible de charger les ventes POS récentes."
          )
        );
      }

      const payload = await response.json();
      const rows = Array.isArray(payload?.orders)
        ? payload.orders
        : Array.isArray(payload)
        ? payload
        : [];

      const normalizedOrders = rows.map((row: any) => ({
        ...row,
        client_name: resolveClientName(row, customers),
      }));

      setOrders(normalizedOrders);
      setSelectedOrderId((current) => {
        if (
          current &&
          normalizedOrders.some((order: PosOrderSummary) => order.id === current)
        ) {
          return current;
        }
        return normalizedOrders[0]?.id ?? null;
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : "Impossible de charger les ventes POS récentes.";
      setListError(errorMessage);
      setOrders([]);
      setSelectedOrderId(null);
      setSelectedOrderDetail(null);
    } finally {
      setLoadingList(false);
    }
  }, [companyId, customers, open, period, warehouseId]);

  const fetchOrderDetail = useCallback(
    async (orderId: number | null) => {
      if (!open || !orderId || !companyId) {
        setSelectedOrderDetail(null);
        return;
      }

      try {
        setLoadingDetail(true);
        setDetailError("");

        const params = new URLSearchParams({
          company_id: String(companyId),
        });

        if (warehouseId) {
          params.set("warehouse_id", String(warehouseId));
        }

        const response = await fetch(
          `${BASE_URL}/api/orders/${orderId}?${params.toString()}`,
          {
            headers: getAuthHeaders(),
          }
        );

        if (!response.ok) {
          throw new Error(
            await getReadableErrorMessage(
              response,
              "Impossible de charger le détail de la vente."
            )
          );
        }

        const detail = await response.json();
        const summary = orders.find((order) => order.id === orderId);
        const items = Array.isArray(detail?.items) ? detail.items : [];
        const payments = Array.isArray(detail?.payments) ? detail.payments : [];

        setSelectedOrderDetail({
          ...detail,
          items,
          payments,
          client_name:
            resolveClientName(detail, customers) ||
            summary?.client_name ||
            "Client inconnu",
          warehouse_name:
            detail?.warehouse_name ||
            summary?.warehouse_name ||
            warehouseData?.name ||
            "Établissement non défini",
        });
      } catch (error) {
        const errorMessage =
          error instanceof Error
            ? error.message
            : "Impossible de charger le détail de la vente.";
        setDetailError(errorMessage);
        setSelectedOrderDetail(null);
      } finally {
        setLoadingDetail(false);
      }
    },
    [companyId, customers, open, orders, warehouseData, warehouseId]
  );

  useEffect(() => {
    if (!open) {
      setSearchValue("");
      setSelectedOrderDetail(null);
      setDetailError("");
      setListError("");
      setPaymentModalOpen(false);
      return;
    }

    void fetchOrders();
  }, [fetchOrders, open]);

  useEffect(() => {
    if (!open || !selectedOrderId) {
      return;
    }

    void fetchOrderDetail(selectedOrderId);
  }, [fetchOrderDetail, open, selectedOrderId]);

  const filteredOrders = useMemo(() => {
    const normalizedQuery = searchValue.trim().toLowerCase();

    if (!normalizedQuery) {
      return orders;
    }

    return orders.filter((order) => {
      const candidates = [
        order.invoice_number,
        order.client_name,
        order.order_date ? dayjs(order.order_date).format("DD/MM/YYYY HH:mm") : "",
        String(order.total || ""),
      ];

      return candidates.some((value) =>
        String(value || "").toLowerCase().includes(normalizedQuery)
      );
    });
  }, [orders, searchValue]);

  const selectedSummary = useMemo(
    () => orders.find((order) => order.id === selectedOrderId) || null,
    [orders, selectedOrderId]
  );

  const remainingAmount = useMemo(() => {
    if (!selectedOrderDetail) return 0;

    const dueAmount = Number(selectedOrderDetail?.due_amount);
    if (Number.isFinite(dueAmount)) {
      return Math.max(0, dueAmount);
    }

    return Math.max(
      0,
      Number(selectedOrderDetail?.total || 0) -
        Number(selectedOrderDetail?.paid_amount || 0)
    );
  }, [selectedOrderDetail]);

  const canAddPayment = useMemo(() => {
    const rawPermissions = user?.permissions;
    const permissions: string[] = Array.isArray(rawPermissions)
      ? rawPermissions
      : [];
    return (
      permissions.includes("Gestion Commerciale.POS.use") ||
      permissions.includes("Gestion Commerciale.Ventes.PaiementsEntrants.create")
    );
  }, [user?.permissions]);

  const shouldShowPaymentAction =
    canAddPayment &&
    Boolean(selectedOrderDetail) &&
    remainingAmount > 0 &&
    selectedOrderDetail?.order_status !== "Annulé";

  const handlePaymentAdded = useCallback(async () => {
    if (!selectedOrderId) {
      return;
    }

    await Promise.all([
      fetchOrders(),
      fetchOrderDetail(selectedOrderId),
    ]);
  }, [fetchOrderDetail, fetchOrders, selectedOrderId]);

  const handleReprintReceipt = useCallback(async () => {
    if (!selectedOrderId) return;

    try {
      setActionLoading("receipt");
      message.loading({
        content: "Préparation du ticket de caisse...",
        key: "pos-reprint-receipt",
      });

      const response = await fetch(
        `${BASE_URL}/api/orders/${selectedOrderId}/receipt`,
        {
          headers: getAuthHeaders(),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getReadableErrorMessage(
            response,
            "Impossible de récupérer le ticket de caisse."
          )
        );
      }

      const receiptData = await response.json();
      onOpenReceipt(receiptData);

      message.success({
        content: "Ticket prêt pour impression.",
        key: "pos-reprint-receipt",
      });
    } catch (error) {
      message.error({
        content:
          error instanceof Error
            ? error.message
            : "Impossible de récupérer le ticket de caisse.",
        key: "pos-reprint-receipt",
      });
    } finally {
      setActionLoading(null);
    }
  }, [onOpenReceipt, selectedOrderId]);

  const handleDownloadInvoice = useCallback(async () => {
    if (!selectedOrderId) return;

    try {
      setActionLoading("invoice");
      message.loading({
        content: "Préparation de la facture...",
        key: "pos-reprint-invoice",
      });

      const detail =
        selectedOrderDetail && Number(selectedOrderDetail.id) === Number(selectedOrderId)
          ? selectedOrderDetail
          : await (async () => {
              const params = new URLSearchParams({
                company_id: String(companyId),
              });
              if (warehouseId) {
                params.set("warehouse_id", String(warehouseId));
              }

              const response = await fetch(
                `${BASE_URL}/api/orders/${selectedOrderId}?${params.toString()}`,
                {
                  headers: getAuthHeaders(),
                }
              );

              if (!response.ok) {
                throw new Error(
                  await getReadableErrorMessage(
                    response,
                    "Impossible de charger la facture."
                  )
                );
              }

              return response.json();
            })();

      const warehouse = await fetchWarehouseDetails(
        Number(detail?.warehouse_id || warehouseId || 0) || null
      );

      const clientOption = customers.find(
        (customer) => Number(customer.ID_Client) === Number(detail?.user_id)
      );

      const customer = clientOption
        ? {
            id: clientOption.ID_Client,
            name: clientOption.Nom_Raison_Sociale,
          }
        : {
            id: detail?.user_id,
            name:
              resolveClientName(detail, customers) ||
              selectedSummary?.client_name ||
              "Client inconnu",
          };

      const invoiceData = {
        ...detail,
        produitsVendus: Array.isArray(detail?.items) ? detail.items : [],
        warehouse,
        payments: Array.isArray(detail?.payments) ? detail.payments : [],
        client: customer,
        created_by_name:
          detail?.created_by_name || detail?.staff_name || "SysAdmin",
        printed_by_name: "Point de vente",
      };

      const fileName = `Facture_${invoiceData.invoice_number}.pdf`;

      const response = await fetch(
        `${BASE_URL}/api/orders/generate-sale-invoice-pdf-react`,
        {
          method: "POST",
          headers: getAuthHeaders(),
          body: JSON.stringify({
            orderData: {
              ...invoiceData,
              clients: [customer],
              produits: (invoiceData.produitsVendus || []).map((item: any) => ({
                id: item.product_id ?? -Number(item.service_id || item.id),
                nom_produit:
                  item.product_name ||
                  item.nom_produit ||
                  `${item.item_type === "service" ? "Service" : "Produit"} ${item.service_id ?? item.product_id ?? item.id ?? ""}`,
                unit_short_name: item.unit_short_name || "unité",
              })),
              taxes: [],
            },
            fileName,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          await getReadableErrorMessage(
            response,
            "Impossible de générer la facture PDF."
          )
        );
      }

      const pdfBlob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");

      link.href = downloadUrl;
      link.download = fileName;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);

      message.success({
        content: "Facture téléchargée avec succès.",
        key: "pos-reprint-invoice",
      });
    } catch (error) {
      message.error({
        content:
          error instanceof Error
            ? error.message
            : "Impossible de générer la facture PDF.",
        key: "pos-reprint-invoice",
      });
    } finally {
      setActionLoading(null);
    }
  }, [
    companyId,
    customers,
    fetchWarehouseDetails,
    selectedOrderDetail,
    selectedOrderId,
    selectedSummary?.client_name,
    warehouseId,
  ]);

  const itemColumns = [
    {
      title: "Article / Service",
      dataIndex: "product_name",
      key: "product_name",
      render: (_: any, record: any) =>
        record.product_name ||
        record.nom_produit ||
        `${record.item_type === "service" ? "Service" : "Produit"} ${record.service_id ?? record.product_id ?? ""}`,
    },
    {
      title: "Qté",
      dataIndex: "quantity",
      key: "quantity",
      width: 80,
      align: "center" as const,
      render: (value: any, record: any) =>
        Number(value ?? record.cartQuantity ?? 0).toLocaleString("fr-FR", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 5,
        }),
    },
    {
      title: "PU",
      dataIndex: "unit_price",
      key: "unit_price",
      width: 120,
      align: "right" as const,
      render: (value: any, record: any) =>
        `${formatAmount(value ?? record.prix_vente)} CFA`,
    },
    {
      title: "Total",
      dataIndex: "subtotal",
      key: "subtotal",
      width: 140,
      align: "right" as const,
      render: (value: any, record: any) => {
        const computedTotal =
          Number(value ?? record.total_price) ||
          Number(record.quantity || 0) * Number(record.unit_price || 0);
        return `${formatAmount(computedTotal)} CFA`;
      },
    },
  ];

  const paymentColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      render: (value: string) =>
        value ? dayjs(value).format("DD/MM/YYYY") : "-",
    },
    {
      title: "Mode",
      dataIndex: "payment_mode_name",
      key: "payment_mode_name",
      render: (value: string) => value || "-",
    },
    {
      title: "Montant",
      dataIndex: "amount",
      key: "amount",
      width: 140,
      align: "right" as const,
      render: (value: any, record: any) =>
        `${formatAmount(
          value ?? record.payment_amount ?? record.amount_applied ?? 0
        )} CFA`,
    },
  ];

  const drawerContentHeight = isMobile ? "auto" : "calc(100vh - 148px)";

  return (
    <Drawer
      title="Centre de réimpression POS"
      open={open}
      onClose={onClose}
      width={isMobile ? "100%" : 1080}
      destroyOnClose={false}
      bodyStyle={{
        padding: 16,
        overflow: isMobile ? "auto" : "hidden",
      }}
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => void fetchOrders()}
          loading={loadingList}
        >
          Actualiser
        </Button>
      }
    >
      {!companyId || !warehouseId ? (
        <Empty description="Sélectionnez un établissement pour consulter les réimpressions POS." />
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "340px minmax(0, 1fr)",
            gap: 16,
            minHeight: 420,
            height: drawerContentHeight,
            maxHeight: drawerContentHeight,
            overflow: isMobile ? "visible" : "hidden",
            alignItems: isMobile ? "stretch" : "start",
          }}
        >
          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: 12,
              background: "#fafafa",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              height: isMobile ? "auto" : drawerContentHeight,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 12,
                width: "100%",
                height: "100%",
                minHeight: 0,
              }}
            >
              <div>
                <Typography.Title level={5} style={{ margin: 0 }}>
                  Retrouver une vente
                </Typography.Title>
                <Typography.Text type="secondary">
                  Recherche rapide dans les ventes POS récentes de l'établissement courant.
                </Typography.Text>
              </div>

              <Input
                allowClear
                prefix={<SearchOutlined />}
                placeholder="N° facture, client, montant..."
                value={searchValue}
                onChange={(event) => setSearchValue(event.target.value)}
              />

              <Space wrap>
                <Button
                  type={period === "today" ? "primary" : "default"}
                  onClick={() => setPeriod("today")}
                >
                  Aujourd&apos;hui
                </Button>
                <Button
                  type={period === "7d" ? "primary" : "default"}
                  onClick={() => setPeriod("7d")}
                >
                  7 jours
                </Button>
                <Button
                  type={period === "30d" ? "primary" : "default"}
                  onClick={() => setPeriod("30d")}
                >
                  30 jours
                </Button>
              </Space>

              {listError ? (
                <div
                  style={{
                    border: "1px solid #ffccc7",
                    background: "#fff2f0",
                    borderRadius: 8,
                    padding: 12,
                  }}
                >
                  <Typography.Text type="danger">{listError}</Typography.Text>
                </div>
              ) : null}

              {loadingList ? (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Spin />
                </div>
              ) : filteredOrders.length === 0 ? (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Empty description="Aucune vente POS trouvée pour ces critères." />
                </div>
              ) : (
                <div
                  style={{
                    flex: 1,
                    minHeight: 0,
                    overflowY: "auto",
                    paddingRight: 4,
                  }}
                >
                  <List
                    dataSource={filteredOrders}
                    split={false}
                    renderItem={(order) => {
                      const isSelected = Number(order.id) === Number(selectedOrderId);

                      return (
                        <List.Item
                          style={{
                            padding: 0,
                            border: "none",
                            marginBottom: 8,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => setSelectedOrderId(order.id)}
                            style={{
                              width: "100%",
                              textAlign: "left",
                              color: "#1f2937",
                              border: isSelected
                                ? "1px solid #91caff"
                                : "1px solid #f0f0f0",
                              background: isSelected ? "#e6f4ff" : "#fff",
                              borderRadius: 8,
                              padding: 12,
                              cursor: "pointer",
                              appearance: "none",
                              WebkitAppearance: "none",
                              font: "inherit",
                              lineHeight: 1.4,
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                gap: 8,
                              }}
                            >
                              <div>
                                <div style={{ fontWeight: 600, color: "#0f172a" }}>
                                  {order.invoice_number}
                                </div>
                                <div style={{ color: "#64748b", fontSize: 12 }}>
                                  {order.client_name}
                                </div>
                              </div>
                              <div
                                style={{
                                  color: "#262626",
                                  fontWeight: 600,
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {formatAmount(order.total)} CFA
                              </div>
                            </div>

                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                gap: 8,
                                marginTop: 10,
                              }}
                            >
                              <span style={{ fontSize: 12, color: "#64748b" }}>
                                {order.order_date
                                  ? dayjs(order.order_date).format("DD/MM/YYYY HH:mm")
                                  : "-"}
                              </span>
                              <div>{getPaymentStatusTag(order.payment_status)}</div>
                            </div>
                          </button>
                        </List.Item>
                      );
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: 8,
              padding: 16,
              minWidth: 0,
              background: "#fff",
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
              height: isMobile ? "auto" : drawerContentHeight,
              maxHeight: isMobile ? "none" : drawerContentHeight,
              overflow: isMobile ? "visible" : "hidden",
              position: isMobile ? "static" : "sticky",
              top: 0,
              alignSelf: isMobile ? "stretch" : "start",
            }}
          >
            {loadingDetail ? (
              <div style={{ textAlign: "center", padding: "64px 0" }}>
                <Spin />
              </div>
            ) : detailError ? (
              <div
                style={{
                  border: "1px solid #ffccc7",
                  background: "#fff2f0",
                  borderRadius: 8,
                  padding: 16,
                }}
              >
                <Typography.Text type="danger">{detailError}</Typography.Text>
              </div>
            ) : selectedOrderDetail ? (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: isMobile ? "visible" : "auto",
                  paddingRight: isMobile ? 0 : 4,
                }}
              >
                <Space direction="vertical" size={16} style={{ width: "100%" }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <Typography.Title level={4} style={{ margin: 0 }}>
                        {selectedOrderDetail.invoice_number}
                      </Typography.Title>
                      <InvoiceNumberCorrection order={selectedOrderDetail} companyId={companyId}
                        warehouseId={selectedOrderDetail.warehouse_id || warehouseId} active={open}
                        onCorrected={async ({ invoice_number }: { invoice_number: string }) => {
                          setSelectedOrderDetail((previous: any) => ({ ...previous, invoice_number }));
                          setOrders(previous => previous.map(item => item.id === selectedOrderId ? { ...item, invoice_number } : item));
                        }} />
                      <Typography.Text type="secondary">
                        Vente POS du{" "}
                        {selectedOrderDetail.order_date
                          ? dayjs(selectedOrderDetail.order_date).format(
                              "DD/MM/YYYY à HH:mm"
                            )
                          : "-"}
                      </Typography.Text>
                    </div>

                    <Space wrap>
                      {shouldShowPaymentAction ? (
                        <Button
                          icon={<PlusCircleOutlined />}
                          onClick={() => setPaymentModalOpen(true)}
                        >
                          Ajouter un paiement
                        </Button>
                      ) : null}
                      <Button
                        icon={<PrinterOutlined />}
                        onClick={() => void handleReprintReceipt()}
                        loading={actionLoading === "receipt"}
                      >
                        Réimprimer le ticket
                      </Button>
                      <Button
                        type="primary"
                        icon={<FilePdfOutlined />}
                        onClick={() => void handleDownloadInvoice()}
                        loading={actionLoading === "invoice"}
                      >
                        Télécharger la facture
                      </Button>
                    </Space>
                  </div>

                  <Descriptions
                    bordered
                    size="small"
                    column={isMobile ? 1 : 2}
                    labelStyle={{ width: isMobile ? undefined : 180 }}
                  >
                    <Descriptions.Item label="Client">
                      {selectedOrderDetail.client_name || selectedSummary?.client_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Établissement">
                      {selectedOrderDetail.warehouse_name}
                    </Descriptions.Item>
                    <Descriptions.Item label="Statut vente">
                      {getOrderStatusTag(selectedOrderDetail.order_status)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Statut paiement">
                      {getPaymentStatusTag(selectedOrderDetail.payment_status)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Montant payé">
                      {formatAmount(selectedOrderDetail.paid_amount)} CFA
                    </Descriptions.Item>
                    <Descriptions.Item label="Montant restant">
                      {formatAmount(remainingAmount)} CFA
                    </Descriptions.Item>
                    <Descriptions.Item label="Total TTC">
                      <strong>{formatAmount(selectedOrderDetail.total)} CFA</strong>
                    </Descriptions.Item>
                    <Descriptions.Item label="Paiements liés">
                      {Array.isArray(selectedOrderDetail.payments)
                        ? selectedOrderDetail.payments.length
                        : 0}
                    </Descriptions.Item>
                  </Descriptions>

                  <Divider style={{ margin: "8px 0" }}>Articles vendus</Divider>

                  <Table
                    dataSource={selectedOrderDetail.items || []}
                    columns={itemColumns}
                    rowKey={(record: any, index) =>
                      String(
                        record?.id ??
                          `${record?.item_type || "product"}-${record?.service_id ?? record?.product_id ?? "item"}-${index ?? 0}`
                      )
                    }
                    size="small"
                    pagination={false}
                    scroll={isMobile ? undefined : { y: 240 }}
                    locale={{
                      emptyText: "Aucun article trouvé pour cette vente.",
                    }}
                  />

                  <Divider style={{ margin: "8px 0" }}>Paiements</Divider>

                  <Table
                    dataSource={selectedOrderDetail.payments || []}
                    columns={paymentColumns}
                    rowKey={(record: any, index) =>
                      String(record?.id ?? `${record?.payment_number ?? "payment"}-${index ?? 0}`)
                    }
                    size="small"
                    pagination={false}
                    locale={{
                      emptyText: "Aucun paiement associé à cette vente.",
                    }}
                  />
                </Space>
              </div>
            ) : (
              <Empty description="Sélectionnez une vente pour consulter ses détails." />
            )}
          </div>
        </div>
      )}

      {selectedOrderDetail ? (
        <VentePaymentForm
          visible={paymentModalOpen}
          onClose={() => setPaymentModalOpen(false)}
          order={selectedOrderDetail}
          onPaymentAdded={handlePaymentAdded}
        />
      ) : null}
    </Drawer>
  );
};

export default PosReprintCenter;
